/**
 * Generador de documentos PDF para reportes
 */
import { jsPDF } from 'jspdf';

class PDFGenerator {
    constructor(options = {}) {
        this.options = {
            tableFontSize: options.tableFontSize || 8,
            ...options
        };
        this.membreteManager = null;
        this.textUtils = null;
    }

    /**
     * Crear nuevo documento PDF
     */
    createDocument(config) {
        return new jsPDF({
            orientation: config.orientation,
            unit: 'mm',
            format: 'A4'
        });
    }

    /**
     * Obtener dimensiones de página
     */
    getPageDimensions(pdf) {
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const isPortrait = pageWidth < pageHeight;

        return { pageWidth, pageHeight, isPortrait };
    }

    /**
     * Agregar título principal al PDF
     */
    addMainTitle(pdf, title, yPosition, pageWidth, isPortrait = false) {
        const titleFontSize = isPortrait ? 14 : 18;
        pdf.setFontSize(titleFontSize);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(title, pageWidth / 2, yPosition, { align: 'center' });

        return yPosition + (isPortrait ? 6 : 10);
    }


    async generateReportPDF(content, config, membreteManager, textUtils) {
        console.log('🎯 PDFGenerator.generateReportPDF() iniciado');

        // Almacenar referencias
        this.membreteManager = membreteManager;
        this.textUtils = textUtils;

        const pdf = this.createDocument(config);
        const { pageWidth, pageHeight, isPortrait } = this.getPageDimensions(pdf);
        let yPosition = 20;

        console.log(`📄 INICIANDO PDF: ${pageWidth}x${pageHeight}mm (${config.orientation})`);
        console.log(`📐 Orientación detectada: ${isPortrait ? 'VERTICAL' : 'HORIZONTAL'}`);

        // ✅ CARGAR Y APLICAR MEMBRETE
        const membreteBase64 = await membreteManager.loadMembreteImage(isPortrait);
        this.currentMembreteBase64 = membreteBase64;

        if (membreteBase64) {
            membreteManager.addMembreteAsBackground(pdf, membreteBase64);
            yPosition = 35;
            console.log('✅ Membrete agregado como fondo - Posición Y ajustada a:', yPosition);
        } else {
            console.warn('⚠️ Continuando sin membrete de fondo...');
            yPosition = 15;
        }

        // ✅ AGREGAR TÍTULOS
        yPosition = this.addMainTitle(pdf, config.title, yPosition, pageWidth, isPortrait);
        yPosition = this.addSubtitle(pdf, yPosition, pageWidth, isPortrait);

        // ✅ AGREGAR SECCIONES
        if (content.sections && content.sections.length > 0) {
            yPosition = await this.addSectionsToPDF(pdf, content.sections, yPosition, pageWidth, pageHeight, membreteBase64);
        }

        // ✅ AGREGAR TABLA
        if (content.nativeTableData) {
            // ✅ USAR MÉTODO ESPECÍFICO PARA BITÁCORAS SI ES NECESARIO
            if (content.nativeTableData.isBitacoraTable) {
                yPosition = await this.addNativeTableToPDF_Bitacoras(pdf, content.nativeTableData, yPosition, pageWidth, pageHeight, textUtils);
            } else {
                yPosition = await this.addNativeTableToPDF(pdf, content.nativeTableData, yPosition, pageWidth, pageHeight, textUtils);
            }
        } else if (content.table) {
            yPosition = await this.addImageTableToPDF(pdf, content.table, yPosition, pageWidth, pageHeight);
        }

        // ✅ AGREGAR NOTAS
        if (config.notes) {
            yPosition = await this.addNotesToPDF(pdf, config.notes, yPosition, pageWidth, pageHeight, membreteBase64);
        }

        // ✅ AGREGAR FOOTER
        this.addFooterToPDF(pdf, isPortrait);

        // ✅ LIMPIEZA
        delete this.currentMembreteBase64;
        this.membreteManager = null;
        this.textUtils = null;

        console.log(`✅ PDF generado (${isPortrait ? 'VERTICAL' : 'HORIZONTAL'}) con ${pdf.internal.pages.length - 1} páginas`);

        return this.createPDFBlob(pdf);
    }

    // ✅ AGREGAR MÉTODO PARA PROCESAR MÚLTIPLES SECCIONES
    async addSectionsToPDF(pdf, sections, yPosition, pageWidth, pageHeight, membreteBase64) {
        const filteredSections = sections.filter(section => {
            const title = section.title || '';
            const isProblematic = title.includes('N/A / Abogados') ||
                title.includes('TRAILER / TRAILER') ||
                (title.includes('Alertas por vehículo') && title.includes('•'));
            return !isProblematic;
        });

        console.log(`📊 Secciones a incluir: ${filteredSections.length}`);

        for (let i = 0; i < filteredSections.length; i++) {
            const section = filteredSections[i];
            yPosition = await this.addSectionToPDF(pdf, section, yPosition, pageWidth, pageHeight, i === 0, membreteBase64);
        }

        return yPosition;
    }

    // ✅ AGREGAR FOOTER
    addFooterToPDF(doc, isPortrait) {

        // Obtener la cantidad de paginas del pdf disponibles 
        const pageCount = doc.getNumberOfPages();

        //Agregar el numero de pagina por cada hoja del doc
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            const pageHeight = doc.internal.pageSize.height;
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(10);

            //Valida la orientacion del footer
            if (isPortrait) {
                //Modo Vertical
                doc.text(`${i}`, pageWidth - 19, pageHeight - 20, { align: "right" });
            }
            else {
                //Modo Horizontal
                doc.text(`${i}`, pageWidth - 5, pageHeight - 16, { align: "right" });
            }
        }
    }

    // ✅ AGREGAR TABLA NATIVA AL PDF (GENERAL)
    async addNativeTableToPDF(pdf, tableData, yPosition, pageWidth, pageHeight, textUtils) {
        if (!tableData || !tableData.rows || tableData.rows.length === 0) {
            return yPosition;
        }

        const isPortrait = pageWidth < pageHeight;
        const margin = 10;
        const footerSpace = 30;
        const titleSpace = isPortrait ? 3 : 4; // Mínimo espacio después del título

        //console.log(`📋 PROCESANDO TABLA NATIVA (${isPortrait ? 'VERTICAL' : 'HORIZONTAL'})...`);
        //console.log(`📐 Posición Y inicial: ${yPosition}`);

        // ✅ NUEVA PÁGINA SIEMPRE SI HAY CONTENIDO PREVIO
        if (yPosition > 50) { // Si ya hay contenido (gráficos/secciones), nueva página
            console.log(`📄 Nueva página para tabla (hay contenido previo)`);
            pdf.addPage();

            if (this.currentMembreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
            }

            yPosition = 35; // Posición inicial en nueva página
        }

        // ✅ TÍTULO DE LA TABLA
        const tableTitleFontSize = isPortrait ? 10 : 14;
        pdf.setFontSize(tableTitleFontSize);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(tableData.title, margin, yPosition);
        yPosition += tableTitleFontSize * 0.4; // Espacio proporcional al título
        yPosition += titleSpace; // Espacio mínimo

        // ✅ CALCULAR LAYOUT CON ESPACIO REAL DISPONIBLE
        const tableLayout = this.calculateTableLayout(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait);

        // ✅ GENERAR TABLA CON PAGINACIÓN
        yPosition = await this.generateTableWithPagination(pdf, tableData, tableLayout, textUtils, yPosition);

        return yPosition;
    }

    // ✅ AGREGAR TABLA NATIVA AL PDF (ESPECÍFICO PARA BITÁCORAS)
    async addNativeTableToPDF_Bitacoras(pdf, tableData, yPosition, pageWidth, pageHeight, textUtils) {
        if (!tableData || !tableData.rows || tableData.rows.length === 0) {
            return yPosition;
        }

        const isPortrait = pageWidth < pageHeight;
        const margin = 10;
        const footerSpace = 30;
        const titleSpace = isPortrait ? 3 : 4; // Mínimo espacio después del título

        // ✅ NUEVA PÁGINA SOLO SI HAY CONTENIDO PREVIO SIGNIFICATIVO (ESPECÍFICO PARA BITÁCORAS)
        const hasActualContent = yPosition > 100;

        if (hasActualContent) {
            pdf.addPage();

            if (this.currentMembreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
            }

            yPosition = 35; // Posición inicial en nueva página
        }

        // ✅ TÍTULO DE LA TABLA
        const tableTitleFontSize = isPortrait ? 10 : 14;
        pdf.setFontSize(tableTitleFontSize);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(tableData.title, margin, yPosition);
        yPosition += tableTitleFontSize * 0.4; // Espacio proporcional al título
        yPosition += titleSpace; // Espacio mínimo

        // ✅ CALCULAR LAYOUT CON ESPACIO REAL DISPONIBLE
        const tableLayout = this.calculateTableLayout(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait);

        // ✅ GENERAR TABLA CON PAGINACIÓN
        yPosition = await this.generateTableWithPagination(pdf, tableData, tableLayout, textUtils, yPosition);

        return yPosition;
    }

    async addNativeTableToPDF_respaldo_0101(pdf, tableData, yPosition, pageWidth, pageHeight, textUtils) {
        if (!tableData || !tableData.rows || tableData.rows.length === 0) {
            return yPosition;
        }

        const isPortrait = pageWidth < pageHeight;
        const margin = 10;
        // const usableWidth = pageWidth - (margin * 2); // No se usa directamente aquí, se usa en tableLayout

        // ✅ TAMAÑOS OPTIMIZADOS SEGÚN ORIENTACIÓN
        // const fontSize = isPortrait ? 6 : this.options.tableFontSize; // Se usa en tableLayout
        // const rowHeight = isPortrait ? fontSize + 2 : fontSize + 4; // Se usa en tableLayout
        // const headerHeight = isPortrait ? fontSize + 3 : fontSize + 6; // Se usa en tableLayout
        const footerSpace = 10;
        // ANTES: const titleSpace = isPortrait ? 8 : 12;
        const titleSpace = isPortrait ? 4 : 6; // AJUSTADO: Reducir el espacio después del título de la tabla

        console.log(`📋 PROCESANDO TABLA NATIVA (${isPortrait ? 'VERTICAL' : 'HORIZONTAL'})...`);

        // ✅ VERIFICAR ESPACIO DISPONIBLE
        // Se calcula el espacio necesario para el título y al menos una fila de encabezado y una de datos.
        const minSpaceForTableTitleAndHeaders = (isPortrait ? 10 : 14) + titleSpace + (isPortrait ? 6 + 3 : this.options.tableFontSize + 6) + (isPortrait ? 6 + 2 : this.options.tableFontSize + 4);
        if (pageHeight - yPosition - footerSpace < minSpaceForTableTitleAndHeaders) {
            console.log(`📄 Nueva página necesaria para tabla nativa (espacio insuficiente para título y encabezados)`);
            pdf.addPage();

            if (this.currentMembreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
            }

            yPosition = 35; // Posición Y inicial en nueva página
        }
        // No agregar titleSpace aquí, se agrega después de dibujar el título.

        // ✅ TÍTULO DE LA TABLA
        const tableTitleFontSize = isPortrait ? 10 : 14;
        pdf.setFontSize(tableTitleFontSize);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(tableData.title, margin, yPosition);
        yPosition += tableTitleFontSize * 0.5; // Espacio basado en el tamaño de la fuente del título
        yPosition += titleSpace; // Aplicar el titleSpace AJUSTADO

        // ✅ CALCULAR LAYOUT DE TABLA
        // Pasar la yPosition actualizada para que el cálculo de maxRowsPerPage sea correcto
        const tableLayout = this.calculateTableLayout(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait);

        // ✅ GENERAR TABLA CON PAGINACIÓN
        // La yPosition para generateTableWithPagination debe ser la que está justo antes de dibujar los encabezados.
        // El método addHeaders dentro de generateTableWithPagination manejará la yPosition para los encabezados.
        yPosition = await this.generateTableWithPagination(pdf, tableData, tableLayout, textUtils, yPosition); // Pasar yPosition actual

        return yPosition;
    }

    async addNativeTableToPDF_backup(pdf, tableData, yPosition, pageWidth, pageHeight, textUtils) {
        if (!tableData || !tableData.rows || tableData.rows.length === 0) {
            return yPosition;
        }

        const isPortrait = pageWidth < pageHeight;
        const margin = 10;
        const usableWidth = pageWidth - (margin * 2);

        // ✅ TAMAÑOS OPTIMIZADOS SEGÚN ORIENTACIÓN
        const fontSize = isPortrait ? 6 : this.options.tableFontSize;
        const rowHeight = isPortrait ? fontSize + 2 : fontSize + 4;
        const headerHeight = isPortrait ? fontSize + 3 : fontSize + 6;
        const footerSpace = 30;
        const titleSpace = isPortrait ? 8 : 12;

        console.log(`📋 PROCESANDO TABLA NATIVA (${isPortrait ? 'VERTICAL' : 'HORIZONTAL'})...`);

        // ✅ VERIFICAR ESPACIO DISPONIBLE
        const availableSpace = pageHeight - yPosition - footerSpace - 100;
        if (availableSpace < 100) {
            console.log(`📄 Nueva página necesaria para tabla nativa`);
            pdf.addPage();

            if (this.currentMembreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
            }

            yPosition = 35;
        } else {
            yPosition += titleSpace;
        }

        // ✅ TÍTULO DE LA TABLA
        pdf.setFontSize(isPortrait ? 10 : 14);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(tableData.title, margin, yPosition);
        yPosition += titleSpace;

        // ✅ CALCULAR LAYOUT DE TABLA
        const tableLayout = this.calculateTableLayout(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait);

        // ✅ GENERAR TABLA CON PAGINACIÓN
        yPosition = await this.generateTableWithPagination(pdf, tableData, tableLayout, textUtils);

        return yPosition;
    }

    calculateTableLayout(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait) {
        const margin = 10;
        const usableWidth = pageWidth - (margin * 2);
        const numColumns = Math.max(tableData.headers.length, ...tableData.rows.map(row => row.length));

        // ✅ CALCULAR ANCHO DE COLUMNAS SEGÚN ORIENTACIÓN
        let columnWidth;
        let columnsToShow;

        if (isPortrait && numColumns > 5) {
            columnsToShow = 5;
            columnWidth = usableWidth / columnsToShow;
        } else {
            columnsToShow = numColumns;
            columnWidth = usableWidth / numColumns;
        }

        // ✅ PERMITIR PESOS PERSONALIZADOS POR COLUMNA (p. ej. para direcciones más anchas)
        const weightsSource = (Array.isArray(tableData.columnWeights) && tableData.columnWeights.length > 0)
            ? tableData.columnWeights
            : (Array.isArray(this.options.tableColumnWeights) ? this.options.tableColumnWeights : null);

        let columnWidths = [];
        let columnPositions = [];

        if (weightsSource && weightsSource.length > 0) {
            const normalizedWeights = weightsSource
                .slice(0, columnsToShow)
                .map(weight => {
                    const numericWeight = Number(weight);
                    return Number.isFinite(numericWeight) && numericWeight > 0 ? numericWeight : 1;
                });

            const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0) || columnsToShow;
            let currentX = margin;

            normalizedWeights.forEach(weight => {
                const width = usableWidth * (weight / totalWeight);
                columnWidths.push(width);
                columnPositions.push(currentX);
                currentX += width;
            });

            // ✅ SI FALTAN COLUMNAS (weights más cortos), completar con anchos uniformes
            if (columnWidths.length < columnsToShow) {
                const usedWidth = columnWidths.reduce((sum, width) => sum + width, 0);
                const remainingColumns = columnsToShow - columnWidths.length;
                const remainingWidth = Math.max(0, usableWidth - usedWidth);
                const fallbackWidth = remainingColumns > 0 ? remainingWidth / remainingColumns : 0;
                let fallbackX = margin + usedWidth;

                for (let i = 0; i < remainingColumns; i++) {
                    columnWidths.push(fallbackWidth);
                    columnPositions.push(fallbackX);
                    fallbackX += fallbackWidth;
                }
            }
        } else {
            const width = usableWidth / columnsToShow;
            columnWidths = Array(columnsToShow).fill(width);
            columnPositions = columnWidths.map((_, index) => margin + (index * width));
        }

        // ✅ TAMAÑOS DE FUENTE Y ALTURA OPTIMIZADOS
        const fontSize = isPortrait ? 6 : this.options.tableFontSize;
        const rowHeight = isPortrait ? 8 : 10; // REDUCIDO para más filas
        const headerHeight = isPortrait ? 10 : 12; // REDUCIDO

        // ✅ CALCULAR ESPACIO REAL DISPONIBLE PARA FILAS
        const spaceForFooter = 10; // Reducido de 30
        const bottomMargin = 10;
        const availableHeightForRows = pageHeight - yPosition - headerHeight - spaceForFooter - bottomMargin;
        const maxRowsPerPage = Math.max(1, Math.floor(availableHeightForRows / rowHeight)); // ✅ MÍNIMO 1 FILA

        return {
            margin,
            usableWidth,
            columnWidth,
            columnWidths,
            columnPositions,
            columnsToShow,
            maxRowsPerPage,
            fontSize,
            rowHeight,
            headerHeight,
            isPortrait
        };
    }

    calculateTableLayout_respaldo08062025(pdf, tableData, yPosition, pageWidth, pageHeight, isPortrait) {
        const margin = 10;
        const usableWidth = pageWidth - (margin * 2);
        const numColumns = Math.max(tableData.headers.length, ...tableData.rows.map(row => row.length));

        // ✅ CALCULAR ANCHO DE COLUMNAS SEGÚN ORIENTACIÓN
        let columnWidth;
        let columnsToShow;

        if (isPortrait && numColumns > 5) {
            columnsToShow = 5;
            columnWidth = usableWidth / columnsToShow;
        } else {
            columnsToShow = numColumns;
            columnWidth = usableWidth / numColumns;
        }

        // ✅ CALCULAR FILAS POR PÁGINA
        const fontSize = isPortrait ? 6 : this.options.tableFontSize;
        const rowHeight = isPortrait ? fontSize + 2 : fontSize + 4;
        const headerHeight = isPortrait ? fontSize + 3 : fontSize + 6;
        const availableHeightForRows = pageHeight - yPosition - 30 - headerHeight - 10;
        const maxRowsPerPage = Math.floor(availableHeightForRows / rowHeight);

        console.log(`📋 LAYOUT CALCULADO:`);
        console.log(`   - Columnas a mostrar: ${columnsToShow}/${numColumns}`);
        console.log(`   - Ancho columna: ${columnWidth.toFixed(1)}mm`);
        console.log(`   - Máx filas por página: ${maxRowsPerPage}`);

        return {
            margin,
            usableWidth,
            columnWidth,
            columnsToShow,
            maxRowsPerPage,
            fontSize,
            rowHeight,
            headerHeight,
            isPortrait
        };
    }

    // ✅ GENERAR TABLA CON PAGINACIÓN
    async generateTableWithPagination(pdf, tableData, layout, textUtils, initialYPosition) {
        let yPosition = initialYPosition;
        let rowCount = 0;

        // ✅ HELPERS PARA ANCHO/POSICIÓN DE COLUMNA (SOPORTA PESOS PERSONALIZADOS)
        const getColumnWidth = (index) => {
            if (Array.isArray(layout.columnWidths) && layout.columnWidths[index] !== undefined) {
                return layout.columnWidths[index];
            }
            return layout.columnWidth;
        };

        const getColumnStartX = (index) => {
            if (Array.isArray(layout.columnPositions) && layout.columnPositions[index] !== undefined) {
                return layout.columnPositions[index];
            }
            return layout.margin + (index * layout.columnWidth);
        };

        // ✅ FUNCIÓN PARA AGREGAR ENCABEZADOS
        const addHeaders = () => {
            if (tableData.headers.length > 0) {
                pdf.setFontSize(layout.fontSize);
                pdf.setFont(undefined, 'bold');

                // Fondo de encabezados
                pdf.setFillColor(233, 236, 239);
                pdf.rect(layout.margin, yPosition, layout.usableWidth, layout.headerHeight, 'F');

                // Texto de encabezados
                pdf.setTextColor(0, 0, 0);

                const headersToShow = layout.isPortrait && tableData.headers.length > layout.columnsToShow ?
                    tableData.headers.slice(0, layout.columnsToShow) :
                    tableData.headers;

                headersToShow.forEach((header, index) => {
                    const columnWidth = getColumnWidth(index);
                    const x = getColumnStartX(index) + (columnWidth / 2);
                    const maxHeaderWidth = Math.max(2, columnWidth - 2);
                    const text = textUtils.truncateText(pdf, header, maxHeaderWidth);
                    pdf.text(text, x, yPosition + (layout.headerHeight / 2), { align: 'center', baseline: 'middle' });
                });

                yPosition += layout.headerHeight;

                // Línea separadora
                pdf.setDrawColor(200, 200, 200);
                pdf.line(layout.margin, yPosition, pdf.internal.pageSize.getWidth() - layout.margin, yPosition);
                yPosition += 1; // Espacio mínimo después de línea
            }
        };

        // ✅ AGREGAR ENCABEZADOS INICIALES
        addHeaders();

        // ✅ USAR EL maxRowsPerPage CALCULADO (NO RECALCULAR)
        const maxRowsPerPage = layout.maxRowsPerPage;

        // ✅ AGREGAR FILAS DE DATOS
        pdf.setFont(undefined, 'normal');

        for (let rowIndex = 0; rowIndex < tableData.rows.length; rowIndex++) {
            const row = tableData.rows[rowIndex];

            // ✅ NUEVA PÁGINA SI ES NECESARIO
            if (rowCount >= maxRowsPerPage) {
                pdf.addPage();

                if (this.currentMembreteBase64 && this.membreteManager) {
                    this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
                }

                yPosition = 35;
                rowCount = 0;
                addHeaders();
            }

            // ✅ FILA ALTERNADA
            if (rowIndex % 2 === 0) {
                pdf.setFillColor(252, 252, 252);
                pdf.rect(layout.margin, yPosition, layout.usableWidth, layout.rowHeight, 'F');
            }

            // ✅ CONTENIDO DE LA FILA
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(layout.fontSize);

            const cellsToShow = layout.isPortrait && row.length > layout.columnsToShow ?
                row.slice(0, layout.columnsToShow) : row;

            // ✅ DETECTAR FILA MASTER (agrupada): solo las primeras 1-2 celdas tienen contenido, el resto vacías.
            // Se renderiza con colspan simulado: texto continuo ocupando todo el ancho disponible.
            const nonEmptyCount = cellsToShow.filter(c => String(c || '').trim() !== '').length;
            const isMasterRow = cellsToShow.length > 2 && nonEmptyCount <= 2 &&
                String(cellsToShow[0] || '').trim() !== '';

            if (isMasterRow) {
                // Combinar celdas con contenido en un solo texto largo
                const fullText = cellsToShow
                    .filter(c => String(c || '').trim() !== '')
                    .join('  |  ');

                pdf.setFont(undefined, 'bold');
                const maxFullWidth = Math.max(2, layout.usableWidth - 4);
                const text = textUtils.truncateText(pdf, fullText, maxFullWidth);
                // Texto alineado a la izquierda, ocupando el ancho completo
                pdf.text(text, layout.margin + 2, yPosition + (layout.rowHeight / 2), { align: 'left', baseline: 'middle' });
                pdf.setFont(undefined, 'normal');
            } else {
                cellsToShow.forEach((cell, cellIndex) => {
                    if (cellIndex < layout.columnsToShow) {
                        const columnWidth = getColumnWidth(cellIndex);
                        const x = getColumnStartX(cellIndex) + (columnWidth / 2);
                        const maxCellWidth = Math.max(2, columnWidth - 2);
                        const text = textUtils.truncateText(pdf, String(cell || ''), maxCellWidth);
                        pdf.text(text, x, yPosition + (layout.rowHeight / 2), { align: 'center', baseline: 'middle' });
                    }
                });
            }

            yPosition += layout.rowHeight;
            rowCount++;
        }

        //console.log(`✅ Tabla completada: ${tableData.rows.length} filas`);

        return yPosition + 5;
    }

    async generateTableWithPagination_08062025(pdf, tableData, layout, textUtils, initialYPosition) {
        // ANTES: let yPosition = pdf.internal.pageSize.getHeight() - pdf.internal.pageSize.getHeight() + layout.margin + 60;
        let yPosition = initialYPosition; // Usar la yPosition pasada como argumento
        let rowCount = 0;

        // ✅ FUNCIÓN PARA AGREGAR ENCABEZADOS
        const addHeaders = () => {
            if (tableData.headers.length > 0) {
                pdf.setFontSize(layout.fontSize);
                pdf.setFont(undefined, 'bold');

                // Fondo de encabezados
                pdf.setFillColor(233, 236, 239); // Gris claro
                // Ajustar la y para el rectángulo del fondo del header para que no sobreescriba el título
                pdf.rect(layout.margin, yPosition - layout.fontSize - 1, layout.usableWidth, layout.headerHeight, 'F');

                // Texto de encabezados
                pdf.setTextColor(0, 0, 0); // Negro

                const headersToShow = layout.isPortrait && tableData.headers.length > layout.columnsToShow ?
                    tableData.headers.slice(0, layout.columnsToShow) :
                    tableData.headers;

                headersToShow.forEach((header, index) => {
                    const x = layout.margin + (index * layout.columnWidth) + (layout.columnWidth / 2); // Centrar texto en celda
                    const maxHeaderWidth = layout.columnWidth - 2; // Pequeño padding
                    const text = textUtils.truncateText(pdf, header, maxHeaderWidth);
                    // Ajustar la y para el texto del header
                    pdf.text(text, x, yPosition, { align: 'center', baseline: 'middle' });
                });

                yPosition += layout.headerHeight;

                // Línea separadora debajo de encabezados
                pdf.setDrawColor(200, 200, 200); // Gris más oscuro para la línea
                pdf.line(layout.margin, yPosition, pdf.internal.pageSize.getWidth() - layout.margin, yPosition);
                yPosition += 2; // Pequeño espacio después de la línea
            }
        };

        // ✅ AGREGAR ENCABEZADOS INICIALES
        addHeaders();

        // ✅ RECALCULAR FILAS POR PÁGINA BASADO EN LA YPOSITION DESPUÉS DE LOS PRIMEROS ENCABEZADOS
        const finalAvailableHeight = pdf.internal.pageSize.getHeight() - yPosition - layout.margin - 20; // Espacio para footer y margen inferior
        const finalMaxRowsPerPage = Math.max(1, Math.floor(finalAvailableHeight / layout.rowHeight)); // Asegurar al menos 1

        console.log(`📋 FILAS FINALES POR PÁGINA: ${finalMaxRowsPerPage}`);

        // ✅ AGREGAR FILAS DE DATOS
        pdf.setFont(undefined, 'normal');

        for (let rowIndex = 0; rowIndex < tableData.rows.length; rowIndex++) {
            const row = tableData.rows[rowIndex];

            // ✅ NUEVA PÁGINA SI ES NECESARIO
            if (rowCount >= finalMaxRowsPerPage) {
                console.log(`📄 Nueva página necesaria: Fila ${rowIndex + 1}/${tableData.rows.length}`);

                pdf.addPage();

                if (this.currentMembreteBase64 && this.membreteManager) {
                    this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
                }

                yPosition = 35; // Posición Y inicial en nueva página
                rowCount = 0;
                addHeaders(); // Agregar encabezados en la nueva página
            }

            // ✅ FILA ALTERNADA
            if (rowIndex % 2 === 0) {
                pdf.setFillColor(252, 252, 252); // Un gris muy claro para filas pares
                pdf.rect(layout.margin, yPosition - layout.rowHeight + (layout.rowHeight - layout.fontSize) / 2 - 1, layout.usableWidth, layout.rowHeight, 'F');
            }

            // ✅ CONTENIDO DE LA FILA
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(layout.fontSize);

            const cellsToShow = layout.isPortrait && row.length > layout.columnsToShow ?
                row.slice(0, layout.columnsToShow) : row;

            cellsToShow.forEach((cell, cellIndex) => {
                if (cellIndex < layout.columnsToShow) {
                    const x = layout.margin + (cellIndex * layout.columnWidth) + (layout.columnWidth / 2); // Centrar texto
                    const maxCellWidth = layout.columnWidth - 2; // Pequeño padding
                    const text = textUtils.truncateText(pdf, String(cell || ''), maxCellWidth);
                    pdf.text(text, x, yPosition - (layout.rowHeight - layout.fontSize) / 2, { align: 'center', baseline: 'middle' });
                }
            });

            yPosition += layout.rowHeight;
            rowCount++;

            // ✅ LÍNEA SEPARADORA SUTIL entre filas (opcional)
            // if (rowIndex < tableData.rows.length - 1) {
            //     pdf.setDrawColor(240, 240, 240); // Gris muy, muy claro
            //     pdf.line(layout.margin, yPosition - layout.rowHeight, pdf.internal.pageSize.getWidth() - layout.margin, yPosition - layout.rowHeight);
            // }
        }

        const totalPages = Math.ceil(tableData.rows.length / finalMaxRowsPerPage);
        console.log(`✅ Tabla completada: ${tableData.rows.length} filas en ${totalPages > 0 ? totalPages : 1} páginas`);

        return yPosition + 5; // Espacio final después de la tabla
    }


    async generateTableWithPagination_backup(pdf, tableData, layout, textUtils) {
        let yPosition = pdf.internal.pageSize.getHeight() - pdf.internal.pageSize.getHeight() + layout.margin + 60;
        let rowCount = 0;

        // ✅ FUNCIÓN PARA AGREGAR ENCABEZADOS
        const addHeaders = () => {
            if (tableData.headers.length > 0) {
                pdf.setFontSize(layout.fontSize);
                pdf.setFont(undefined, 'bold');

                // Fondo de encabezados
                pdf.setFillColor(233, 236, 239);
                pdf.rect(layout.margin, yPosition - 1, layout.usableWidth, layout.headerHeight, 'F');

                // Texto de encabezados
                pdf.setTextColor(0, 0, 0);

                const headersToShow = layout.isPortrait && tableData.headers.length > layout.columnsToShow ?
                    tableData.headers.slice(0, layout.columnsToShow) :
                    tableData.headers;

                headersToShow.forEach((header, index) => {
                    const x = layout.margin + (index * layout.columnWidth) + 1;
                    const maxHeaderWidth = layout.columnWidth - 3;
                    const text = textUtils.truncateText(pdf, header, maxHeaderWidth);
                    pdf.text(text, x, yPosition + layout.fontSize + 1);
                });

                yPosition += layout.headerHeight;

                // Línea separadora
                pdf.setDrawColor(200, 200, 200);
                pdf.line(layout.margin, yPosition, pdf.internal.pageSize.getWidth() - layout.margin, yPosition);
                yPosition += 2;
            }
        };

        // ✅ AGREGAR ENCABEZADOS INICIALES
        addHeaders();

        // ✅ RECALCULAR FILAS POR PÁGINA
        const finalAvailableHeight = pdf.internal.pageSize.getHeight() - yPosition - 30 - 5;
        const finalMaxRowsPerPage = Math.floor(finalAvailableHeight / layout.rowHeight);

        console.log(`📋 FILAS FINALES POR PÁGINA: ${finalMaxRowsPerPage}`);

        // ✅ AGREGAR FILAS DE DATOS
        pdf.setFont(undefined, 'normal');

        for (let rowIndex = 0; rowIndex < tableData.rows.length; rowIndex++) {
            const row = tableData.rows[rowIndex];

            // ✅ NUEVA PÁGINA SI ES NECESARIO
            if (rowCount >= finalMaxRowsPerPage) {
                console.log(`📄 Nueva página necesaria: Fila ${rowIndex + 1}/${tableData.rows.length}`);

                pdf.addPage();

                if (this.currentMembreteBase64 && this.membreteManager) {
                    this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
                }

                yPosition = 50;
                rowCount = 0;
                addHeaders();
            }

            // ✅ FILA ALTERNADA
            if (rowIndex % 2 === 0) {
                pdf.setFillColor(252, 252, 252);
                pdf.rect(layout.margin, yPosition - 0.5, layout.usableWidth, layout.rowHeight, 'F');
            }

            // ✅ CONTENIDO DE LA FILA
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(layout.fontSize);

            const cellsToShow = layout.isPortrait && row.length > layout.columnsToShow ?
                row.slice(0, layout.columnsToShow) : row;

            cellsToShow.forEach((cell, cellIndex) => {
                if (cellIndex < layout.columnsToShow) {
                    const x = layout.margin + (cellIndex * layout.columnWidth) + 1;
                    const maxCellWidth = layout.columnWidth - 3;
                    const text = textUtils.truncateText(pdf, String(cell || ''), maxCellWidth);
                    pdf.text(text, x, yPosition + layout.fontSize);
                }
            });

            yPosition += layout.rowHeight;
            rowCount++;

            // ✅ LÍNEA SEPARADORA SUTIL
            if (rowIndex < tableData.rows.length - 1) {
                if (!layout.isPortrait || rowIndex % 5 === 4) {
                    pdf.setDrawColor(245, 245, 245);
                    pdf.line(layout.margin, yPosition - 0.5, pdf.internal.pageSize.getWidth() - layout.margin, yPosition - 0.5);
                }
            }
        }

        const totalPages = Math.ceil(tableData.rows.length / finalMaxRowsPerPage);
        console.log(`✅ Tabla completada: ${tableData.rows.length} filas en ${totalPages} páginas`);

        return yPosition + 5;
    }

    // ✅ AGREGAR TABLA COMO IMAGEN AL PDF
    async addImageTableToPDF(pdf, tableCanvas, yPosition, pageWidth, pageHeight) {
        const footerSpace = 30;

        if (yPosition > pageHeight - footerSpace - 60) {
            console.log('📄 Nueva página necesaria para tabla imagen');
            pdf.addPage();

            if (this.currentMembreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, this.currentMembreteBase64);
            }

            yPosition = 35;
        } else {
            yPosition += 15;
        }

        // ✅ TÍTULO
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Tabla de Datos Detallada', 10, yPosition);
        yPosition += 10;

        // ✅ CALCULAR DIMENSIONES
        const maxWidth = pageWidth - 20;
        const maxHeight = pageHeight - yPosition - 30;
        const dimensions = this.calculateOptimalDimensions(tableCanvas, maxWidth, maxHeight);

        // ✅ AGREGAR IMAGEN
        const imgData = tableCanvas.toDataURL('image/jpeg', 0.95);
        const xPos = (pageWidth - dimensions.width) / 2;

        pdf.addImage(imgData, 'JPEG', xPos, yPosition, dimensions.width, dimensions.height);
        yPosition += dimensions.height + 15;

        return yPosition;
    }


    // ✅ CALCULAR DIMENSIONES ÓPTIMAS
    calculateOptimalDimensions(canvas, maxWidth, maxHeight) {
        const aspectRatio = canvas.height / canvas.width;
        let finalWidth = maxWidth;
        let finalHeight = finalWidth * aspectRatio;

        if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = finalHeight / aspectRatio;
        }

        return { width: finalWidth, height: finalHeight };
    }


    /**
     * Agregar subtítulo con fecha
     */
    addSubtitle(pdf, yPosition, pageWidth, isPortrait = false) {
        const subtitleFontSize = isPortrait ? 8 : 12;
        pdf.setFontSize(subtitleFontSize);
        pdf.setFont(undefined, 'normal');
        pdf.text(`Generado: ${new Date().toLocaleString()}`, pageWidth / 2, yPosition, { align: 'center' });

        return yPosition + (isPortrait ? 15 : 25);
    }

    addImageToPDF(pdf, canvas, title, yPosition, pageWidth, pageHeight, membreteBase64 = null) {
        // ✅ Código existente mejorado con manejo de membrete
        const margin = 10;
        const footerSpace = 30;
        const maxWidth = pageWidth - (margin * 2);

        if (yPosition > pageHeight - footerSpace - 100) {
            console.log('📄 Nueva página necesaria para imagen');
            pdf.addPage();

            if (membreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
            }
            yPosition = 35;
        }

        // Título de la sección
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(title, margin, yPosition);
        yPosition += 10;

        // Calcular y agregar imagen
        const dimensions = this.calculateOptimalDimensions(canvas, maxWidth, pageHeight - yPosition - footerSpace - 20);
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const xPos = (pageWidth - dimensions.width) / 2;

        pdf.addImage(imgData, 'JPEG', xPos, yPosition, dimensions.width, dimensions.height);

        return yPosition + dimensions.height + 15;
    }

    async addSectionToPDF(pdf, section, yPosition, pageWidth, pageHeight, isFirstSection = false, membreteBase64 = null) {
        try {
            const canvas = section.canvas;
            if (!canvas) return yPosition;

            console.log(`📄 Agregando sección: ${section.title}`);
            console.log(`📐 Dimensiones canvas: ${canvas.width}x${canvas.height}`);

            // ✅ DETECTAR ORIENTACIÓN Y TIPO
            const isPortrait = pageWidth < pageHeight;
            const isFilaCompleta = section.type === 'fila-completa';
            const isHorizontal = !isPortrait;

            if (isHorizontal && isFilaCompleta) {
                // 🎯 LÓGICA HORIZONTAL: MÁS SIMPLE Y CONSERVADORA
                console.log('📊 Aplicando lógica HORIZONTAL para fila completa');

                // ✅ NUEVA PÁGINA PARA CADA FILA COMPLETA
                if (!isFirstSection) {
                    pdf.addPage();
                    if (membreteBase64 && this.membreteManager) {
                        this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
                    }
                    yPosition = 35;
                }

                // ✅ CALCULAR DIMENSIONES NORMALES (COMO VERTICAL PERO CON MÁS ESPACIO)
                const margin = 10;
                const titleHeight = 20;
                const footerSpace = 30;

                const dimensions = this.calculateOptimalDimensions(
                    canvas,
                    pageWidth - (margin * 2),
                    pageHeight - yPosition - footerSpace - titleHeight
                );

                // 🔧 AJUSTE: Si es muy grande, reducir un poco
                let finalWidth = dimensions.width;
                let finalHeight = dimensions.height;

                // Si ocupa más del 85% de la página, reducir al 80%
                if (finalHeight > (pageHeight - yPosition - footerSpace - titleHeight) * 0.85) {
                    const reduceScale = 0.8;
                    finalWidth = dimensions.width * reduceScale;
                    finalHeight = dimensions.height * reduceScale;
                    console.log(`🔧 Reduciendo escala al 80%: ${finalWidth.toFixed(1)}x${finalHeight.toFixed(1)}`);
                }

                // ✅ TÍTULO
                pdf.setFontSize(14);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(0, 0, 0);
                pdf.text(section.title, pageWidth / 2, yPosition + 15, { align: 'center' });

                // ✅ IMAGEN CENTRADA
                const x = (pageWidth - finalWidth) / 2;
                const y = yPosition + titleHeight;

                const imgData = canvas.toDataURL('image/png', 0.95);
                pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);

                console.log(`✅ Fila completa agregada: ${finalWidth.toFixed(1)}x${finalHeight.toFixed(1)}mm`);
                return y + finalHeight + 15;

            } else {
                // 📱 LÓGICA ORIGINAL PARA VERTICAL (SIN CAMBIOS)
                console.log('📊 Aplicando lógica VERTICAL/INDIVIDUAL (original)');

                const margin = 10;
                const isPortrait = pageWidth < pageHeight;
                const titleHeight = isPortrait ? 15 : 20;
                const footerSpace = 30;

                //const canvas = section.canvas;
                const dimensions = this.calculateOptimalDimensions(canvas, pageWidth - (margin * 2), pageHeight - yPosition - footerSpace);


                // ✅ NUEVA PÁGINA SI ES NECESARIO (LÓGICA ORIGINAL)
                if (!isFirstSection && this.needsNewPage(yPosition, dimensions.height + titleHeight, pageHeight, footerSpace)) {
                    console.log(`📄 Nueva página necesaria para: ${section.title}`);
                    pdf.addPage();

                    if (membreteBase64 && this.membreteManager) {
                        this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
                    }
                    yPosition = 35;
                }
                // if (!isFirstSection && this.needsNewPage(yPosition, dimensions.height + titleHeight, pageHeight, footerSpace)) {
                //     console.log(`📄 Nueva página necesaria para: ${section.title}`);
                //     pdf.addPage();

                //     if (membreteBase64 && this.membreteManager) {
                //         this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
                //     }
                //     yPosition = 35;
                // }

                // ✅ TÍTULO DE LA SECCIÓN (LÓGICA ORIGINAL)
                // const titleFontSize = isPortrait ? 12 : 14;
                // pdf.setFontSize(titleFontSize);
                // pdf.setFont(undefined, 'bold');
                // pdf.setTextColor(0, 0, 0);
                // pdf.text(section.title, margin, yPosition);
                // yPosition += titleHeight * 0.6;

                // if (section.description) {
                //     const descFontSize = isPortrait ? 8 : 10;
                //     pdf.setFontSize(descFontSize);
                //     pdf.setFont(undefined, 'normal');
                //     pdf.text(section.description, margin, yPosition);
                //     yPosition += titleHeight * 0.4;
                // }

                // ✅ AGREGAR IMAGEN CENTRADA (LÓGICA ORIGINAL)
                // const xPos = (pageWidth - dimensions.width) / 2;
                // const imgData = canvas.toDataURL('image/jpeg', 0.95);
                // pdf.addImage(imgData, 'JPEG', xPos, yPosition, dimensions.width, dimensions.height);
                const xPos = (pageWidth - dimensions.width) / 2;
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                pdf.addImage(imgData, 'JPEG', xPos, yPosition, dimensions.width, dimensions.height);

                console.log(`✅ Sección agregada: ${section.title} (${dimensions.width.toFixed(1)}x${dimensions.height.toFixed(1)}mm)`);

                return yPosition + dimensions.height + 15;
            }

        } catch (error) {
            console.error('❌ Error agregando sección al PDF:', error);
            return yPosition;
        }
    }


    addSectionToPDF_original(pdf, section, yPosition, pageWidth, pageHeight, isFirstSection = false, membreteBase64 = null) {
        // ✅ Mejorar método existente con mejor manejo de membrete
        const margin = 10;
        const isPortrait = pageWidth < pageHeight;
        const titleHeight = isPortrait ? 15 : 20;
        const footerSpace = 30;

        console.log(`📐 Agregando sección en orientación: ${isPortrait ? 'VERTICAL' : 'HORIZONTAL'}`);

        const canvas = section.canvas;
        const dimensions = this.calculateOptimalDimensions(canvas, pageWidth - (margin * 2), pageHeight - yPosition - footerSpace);

        // ✅ NUEVA PÁGINA SI ES NECESARIO
        if (!isFirstSection && this.needsNewPage(yPosition, dimensions.height + titleHeight, pageHeight, footerSpace)) {
            console.log(`📄 Nueva página necesaria para: ${section.title}`);
            pdf.addPage();

            if (membreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
            }
            yPosition = 35;
        }

        // ✅ TÍTULO Y DESCRIPCIÓN
        // const titleFontSize = isPortrait ? 12 : 14;
        // pdf.setFontSize(titleFontSize);
        // pdf.setFont(undefined, 'bold');
        // pdf.setTextColor(0, 0, 0);
        // pdf.text(section.title, margin, yPosition);
        // yPosition += titleHeight * 0.5;

        // if (section.description) {
        //     const descFontSize = isPortrait ? 8 : 10;
        //     pdf.setFontSize(descFontSize);
        //     pdf.setFont(undefined, 'normal');
        //     pdf.text(section.description, margin, yPosition);
        //     yPosition += titleHeight * 0.5;
        // }

        // ✅ AGREGAR IMAGEN CENTRADA
        const xPos = (pageWidth - dimensions.width) / 2;
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', xPos, yPosition, dimensions.width, dimensions.height);

        console.log(`✅ Sección agregada: ${section.title} (${dimensions.width.toFixed(1)}x${dimensions.height.toFixed(1)}mm)`);

        return yPosition + dimensions.height + 15;
    }

    // ✅ MÉTODO AUXILIAR PARA VERIFICAR SI NECESITA NUEVA PÁGINA
    needsNewPage(currentY, contentHeight, pageHeight, footerSpace) {
        return currentY + contentHeight > pageHeight - footerSpace - 20;
    }

    // ✅ MÉTODOS EXISTENTES MANTENIDOS...
    addNotesToPDF(pdf, notes, yPosition, pageWidth, pageHeight, membreteBase64 = null) {
        // ✅ Código existente mejorado
        const footerSpace = 30;

        if (yPosition > pageHeight - footerSpace - 50) {
            console.log('📄 Nueva página necesaria para notas');
            pdf.addPage();

            if (membreteBase64 && this.membreteManager) {
                this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
            }
            yPosition = 35;
        }

        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Comentarios:', 10, yPosition);
        yPosition += 10;

        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(10);

        const lines = pdf.splitTextToSize(notes, pageWidth - 20);

        lines.forEach(line => {
            if (yPosition > pageHeight - footerSpace - 15) {
                pdf.addPage();
                if (membreteBase64 && this.membreteManager) {
                    this.membreteManager.addMembreteAsBackground(pdf, membreteBase64);
                }
                yPosition = 35;
            }
            pdf.text(line, 10, yPosition);
            yPosition += 5;
        });

        return yPosition;
    }

    debugTablePagination(tableData, maxRowsPerPage, pageHeight, yPosition) {
        // ✅ Código existente mantenido
        const orientation = pageHeight > 200 ? 'HORIZONTAL' : 'VERTICAL';
        const isPortrait = pageHeight < 200;
        const expectedRowsPerPage = isPortrait ?
            Math.floor((pageHeight - 100) / 8) :
            Math.floor((pageHeight - 100) / 12);

        console.log('🔍 DEBUG PAGINACIÓN DE TABLA:');
        console.log(`   📐 ORIENTACIÓN: ${orientation}`);
        console.log(`   📊 Total de filas: ${tableData.rows.length}`);
        console.log(`   📄 Filas por página (calculado): ${maxRowsPerPage}`);
        console.log(`   🎯 Filas esperadas (${orientation}): ~${expectedRowsPerPage}`);
        console.log(`   📋 Páginas estimadas: ${Math.ceil(tableData.rows.length / maxRowsPerPage)}`);
        console.log(`   📏 Altura de página: ${pageHeight}mm`);
        console.log(`   📍 Posición Y inicial: ${yPosition}mm`);

        if (maxRowsPerPage < expectedRowsPerPage * 0.7) {
            console.warn(`⚠️ POSIBLE PROBLEMA: Solo ${maxRowsPerPage} filas vs ${expectedRowsPerPage} esperadas`);
        }

        for (let page = 0; page < Math.ceil(tableData.rows.length / maxRowsPerPage); page++) {
            const startRow = page * maxRowsPerPage;
            const endRow = Math.min(startRow + maxRowsPerPage, tableData.rows.length);
            console.log(`   📃 Página ${page + 1}: Filas ${startRow + 1} - ${endRow}`);
        }
    }

    /**
     * Convertir blob a base64
     */
    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Crear blob del PDF
     */
    createPDFBlob(pdf) {
        return pdf.output('blob');
    }

    /**
     * Establecer referencia al MembreteManager (inyección de dependencia)
     */
    setMembreteManager(membreteManager) {
        this.membreteManager = membreteManager;
    }
}

export default PDFGenerator;